import { Alert } from "@mui/lab";
import {
	AppBar,
	Box,
	Container,
	CssBaseline,
	Grid2,
	Paper,
	Snackbar,
	Toolbar,
	Typography,
	alpha,
} from "@mui/material";
import { get } from "lodash-es";
import React, { useEffect, useRef, useState } from "react";
import { LoginAsGuest } from "./login-strategies/LoginAsGuest";
import { LoginWithEmailAndPassword } from "./login-strategies/LoginWithEmailAndPassword";
import { LoginWithWindowsAuthentication } from "./login-strategies/LoginWithWindowsAuthentication";
import { LoginWithAzureAD } from "./login-strategies/azure-ad/LoginWithAzureAD";
import { LoginParams } from "./models/login.models";

export function Login({ authConfig, initialError }: LoginParams) {
	const params = useRef(new URL(document.location as any).searchParams);
	const [open, setOpen] = useState(false);
	const [message, setMessage] = useState("");
	const [severity, setSeverity] = useState<"info" | "error">("info");

	const useEmailAndPassword = get(authConfig, "useEmailAndPassword", true);
	const useAzureAD = get(authConfig, "useAzureAD", false);
	const useWindowsAuth = get(authConfig, "useWindowsAuth", false);
	const guestIsEnabled = get(authConfig, "guestIsEnabled", false);

	const getBase = () =>
		document.getElementsByTagName("base")[0]?.getAttribute("href") || "/";

	const handleClose = () => {
		setOpen(false);
		// Read current URL params (not the ref) to preserve redirectTo
		const currentParams = new URL(document.location as any).searchParams;
		currentParams.delete("message");
		currentParams.delete("error");
		const base = getBase();
		const queryString = currentParams.toString();
		const newUrl = `${window.location.origin}${base}${queryString ? `?${queryString}` : ""}`;
		window.history.replaceState({ path: newUrl }, "", newUrl);
	};

	useEffect(() => {
		if (initialError) {
			setMessage(initialError);
			setSeverity("error");
			setOpen(true);
		} else if (params.current.has("error")) {
			setMessage(params.current.get("error")!);
			setSeverity("error");
			setOpen(true);
		} else if (params.current.has("message")) {
			setMessage(params.current.get("message")!);
			setSeverity("info");
			setOpen(true);
		}
	}, [initialError]);

	return (
		<Box
			sx={{
				minHeight: "100vh",
				backgroundImage: (theme) =>
					`linear-gradient(135deg, ${
						theme.palette.background.default
					} 0%, ${alpha(theme.palette.primary.main, 0.2)} 100%)`,
				display: "flex",
				flexDirection: "column",
			}}
		>
			<CssBaseline />
			<AppBar
				position="static"
				sx={{
					backgroundColor: (theme) => theme.palette.primary.main,
					boxShadow: "0 4px 20px 0 rgba(0, 0, 0, 0.1)",
				}}
			>
				<Toolbar>
					<Typography variant="h6">
						<Box
							component="a"
							href="/"
							sx={{
								textDecoration: "none",
								color: (theme) => theme.palette.primary.contrastText,
								fontWeight: "bold",
								display: "flex",
								alignItems: "center",
							}}
						>
							<Box
								component="span"
								sx={{
									marginLeft: 1,
									fontWeight: 700,
									letter: "1px",
								}}
							>
								MESF
							</Box>
						</Box>
					</Typography>
				</Toolbar>
			</AppBar>

			<Container
				sx={{
					padding: 4,
					flex: 1,
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
				}}
			>
				<Paper
					elevation={3}
					sx={{
						margin: "40px auto",
						maxWidth: 800,
						borderRadius: 1,
						overflow: "hidden",
						boxShadow: "0 10px 30px 0 rgba(0, 0, 0, 0.1)",
					}}
				>
					<Box
						sx={{
							textAlign: "center",
							padding: 3,
							fontWeight: "bold",
							backgroundColor: (theme) => theme.palette.background.paper,
							borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
						}}
					>
						<Typography variant="h5">Welcome Back</Typography>
						<Typography variant="body2" color="textSecondary">
							Please sign in to your account
						</Typography>
					</Box>

					<Grid2
						container
						justifyContent="space-between"
						alignItems="stretch"
						spacing={2}
						sx={{
							backgroundColor: (theme) => theme.palette.background.default,
						}}
					>
						{!useAzureAD && !guestIsEnabled && !useWindowsAuth ? (
							<Grid2 size={{ xs: 12 }}>
								<Paper
									elevation={0}
									sx={{
										padding: 5,
										margin: 3,
										borderRadius: 1,
										boxShadow: "0 5px 15px 0 rgba(0, 0, 0, 0.05)",
										border: (theme) => `1px solid ${theme.palette.divider}`,
										transition: "all 0.3s ease",
										"&:hover": {
											boxShadow: "0 8px 25px 0 rgba(0, 0, 0, 0.1)",
											transform: "translateY(-5px)",
										},
									}}
								>
									{useEmailAndPassword && <LoginWithEmailAndPassword />}
								</Paper>
							</Grid2>
						) : (
							<>
								<Grid2 size={{ md: 6, xs: 12 }} sx={{ display: "flex" }}>
									<Paper
										elevation={0}
										sx={{
											flex: 1,
											padding: 5,
											margin: 3,
											display: "flex",
											flexDirection: "column",
											justifyContent: "center",
											borderRadius: 1,
											boxShadow: "0 5px 15px 0 rgba(0, 0, 0, 0.05)",
											border: (theme) => `1px solid ${theme.palette.divider}`,
											transition: "all 0.3s ease",
											"&:hover": {
												boxShadow: "0 8px 25px 0 rgba(0, 0, 0, 0.1)",
												transform: "translateY(-5px)",
											},
										}}
									>
										{useEmailAndPassword && <LoginWithEmailAndPassword />}
									</Paper>
								</Grid2>

								<Grid2 size={{ md: 6, xs: 12 }} sx={{ display: "flex" }}>
									<Paper
										elevation={0}
										sx={{
											flex: 1,
											padding: 5,
											margin: 3,
											display: "flex",
											flexDirection: "column",
											justifyContent: "center",
											borderRadius: 1,
											boxShadow: "0 5px 15px 0 rgba(0, 0, 0, 0.05)",
											border: (theme) => `1px solid ${theme.palette.divider}`,
											transition: "all 0.3s ease",
											"&:hover": {
												boxShadow: "0 8px 25px 0 rgba(0, 0, 0, 0.1)",
												transform: "translateY(-5px)",
											},
										}}
									>
										<Box display="flex" flexDirection="column" gap={2}>
											<LoginWithWindowsAuthentication />
											{useAzureAD && (
												<LoginWithAzureAD config={authConfig.AzureConfig} />
											)}
											{guestIsEnabled && <LoginAsGuest />}
										</Box>
									</Paper>
								</Grid2>
							</>
						)}
					</Grid2>
				</Paper>
			</Container>

			<Snackbar
				open={open}
				autoHideDuration={6000}
				onClose={handleClose}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert onClose={handleClose} severity={severity} variant="filled">
					{message}
				</Alert>
			</Snackbar>
		</Box>
	);
}
